import React from 'react';
import I18n from 'i18n-js';
import _get from 'lodash/get';
import { withRouter } from 'react-router';
import { connect } from 'react-redux';
import cx from 'classnames';
import { Button, Spin } from 'grab-ui';
import { shape } from 'prop-types';
import shopifyGrabExpress from 'public/images/shopify-x-grabexpress.png';
import { LAST_SELECTED_SHOP_LOCALSTORAGE_KEY } from 'constants/shopify-constants';
import {
  initConnection,
  completeConnection,
} from '../../../actions/shopify-endpoints';
import { ROUTES_V2_PATHS } from '../../../constants/route-constants';
import {
  GRAB_ID_LOGIN_METHOD,
  STORAGE_SHOPIFY_CALLBACK,
} from '../../../constants/app-constants';
import { getCompany, getUserCountryCode } from '../../../helper/user';
import { userInfoSelector } from '../../../selectors/app-selector';
import styles from './styles.less';
import { isAuthHintEmpty } from '../../../helper/session-helper';

const localeKeyPrefix = 'shopify.landing';

class ShopifyLandingPage extends React.Component {
  static propTypes = {
    location: shape({}).isRequired,
    history: shape({}).isRequired,
    userInfo: shape({}).isRequired,
  };

  constructor(props) {
    super(props);

    const callbackParams = this.getCallbackParamsFromLocalStorage();

    this.state = {
      authorizing: false,
      authorizeURL: '',
      completing: false,
      callbackParams,
      error: null,
    };
  }

  componentDidMount() {
    this.handleAccessFromShopifyDashboard();
    this.processLanding();
    this.processCallbackParams();
    this.processCallback();
  }

  componentDidUpdate(prevProps, prevState, snapshot) {
    if (!prevState.authorizeURL && this.state.authorizeURL) {
      window.location.href = this.state.authorizeURL;
    }

    this.processCallback();
  }

  getAuthorizeParams = () => {
    const query = new URLSearchParams(this.props.location.search);
    const timestamp = +query.get('timestamp');
    if (query.get('code')) {
      return {};
    }
    return {
      hmac: query.get('hmac'),
      shop: query.get('shop'),
      host: query.get('host'),
      timestamp: !isNaN(timestamp) ? timestamp : 0,
    };
  };

  getCallbackParams = () => {
    const query = new URLSearchParams(this.props.location.search);
    const timestamp = +query.get('timestamp');
    return {
      hmac: query.get('hmac'),
      code: query.get('code'),
      host: query.get('host'),
      shop: query.get('shop'),
      state: query.get('state'),
      timestamp: !isNaN(timestamp) ? timestamp : 0,
    };
  };

  getCallbackParamsFromLocalStorage = () => {
    const callbackParamsJSON = window.localStorage.getItem(
      STORAGE_SHOPIFY_CALLBACK,
    );
    if (callbackParamsJSON) {
      window.localStorage.removeItem(STORAGE_SHOPIFY_CALLBACK);
      try {
        return JSON.parse(callbackParamsJSON);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.log('bad callbackParams in local storage');
      }
    }
    return null;
  };

  handleAccessFromShopifyDashboard = () => {
    const query = new URLSearchParams(this.props.location.search);
    const session = query.get('session');
    if (!session) return;
    if (!isAuthHintEmpty()) {
      this.props.history.replace(ROUTES_V2_PATHS.shopifyHome);
      return;
    }
    window.location.href = window.location.origin;
  };

  processLanding = () => {
    const { shop, host, timestamp, hmac } = this.getAuthorizeParams();
    if (hmac && shop && host && timestamp) {
      this.setState({ authorizing: true });
      initConnection(shop, host, timestamp, hmac, false, true)
        .then(({ authorizeURL }) => {
          if (authorizeURL) {
            this.setState({ authorizeURL });
          }
        })
        .catch(async err => {
          const errResponse = await err.json();
          this.setState({ error: errResponse });
        });
    }
  };

  processCallbackParams = () => {
    const callbackParams = this.getCallbackParams();
    const { hmac, code, host, shop, state, timestamp } = callbackParams;
    if (hmac && code && host && shop && state && timestamp) {
      this.setState({
        callbackParams,
      });

      const { history, location } = this.props;
      history.replace(location.pathname);
    }
  };

  redirectLogin = method => () => {
    // save callbackParams to local storage, because need to leave current page to login
    window.localStorage.setItem(
      STORAGE_SHOPIFY_CALLBACK,
      JSON.stringify(this.state.callbackParams),
    );

    // redirect to login
    const searchParams = new URLSearchParams();
    searchParams.set('redirect', ROUTES_V2_PATHS.shopifyLanding);
    if (method === GRAB_ID_LOGIN_METHOD) {
      searchParams.set('method', GRAB_ID_LOGIN_METHOD);
    }
    this.props.history.replace(
      `${ROUTES_V2_PATHS.login}?${searchParams.toString()}`,
    );
  };

  processCallback = () => {
    const { callbackParams } = this.state;
    const { userInfo, history } = this.props;

    const hasUserName = !!_get(userInfo, 'user.name');
    const hasUserSafeID = !!_get(userInfo, 'user.safeID');
    if (!hasUserName || !hasUserSafeID) {
      // User information is not fully populated, don't continue
      return;
    }

    if (!isAuthHintEmpty() && callbackParams) {
      const company = getCompany(userInfo);
      const countryCode = getUserCountryCode(userInfo);
      this.setState({ callbackParams: null, completing: true });
      const { hmac, code, host, shop, state, timestamp } = callbackParams;
      completeConnection(
        code,
        shop,
        host,
        timestamp,
        hmac,
        state,
        company && company.id,
        countryCode,
      )
        .then(({ success }) => {
          if (success) {
            window.localStorage.setItem(
              LAST_SELECTED_SHOP_LOCALSTORAGE_KEY,
              shop,
            );
            history.replace(`${ROUTES_V2_PATHS.shopifyHome}`);
          } else {
            this.setState({
              error: {
                message: I18n.t(`${localeKeyPrefix}.status.unknownError`),
              },
            });
          }
        })
        .catch(async err => {
          const errResponse = await err.json();
          this.setState({ error: errResponse });
        });
    }
  };

  renderShopifyLogin = () => (
    <div className={cx(styles.mainPanelContent, styles.loginPanel)}>
      <div className={styles.encouragement}>
        <h1 className={styles.primaryText}>
          {I18n.t(`${localeKeyPrefix}.encouragement.primaryText`)}
        </h1>
        <h2 className={styles.secondaryText}>
          {I18n.t(`${localeKeyPrefix}.encouragement.secondaryText`)}
        </h2>
      </div>
      <div className={styles.buttons}>
        <Button
          id={'grab-id-login'}
          className={cx(styles.button, styles.grabID)}
          onClick={this.redirectLogin(GRAB_ID_LOGIN_METHOD)}
        >
          <div className={styles.buttonText}>
            <span className={styles.primaryText}>
              {I18n.t(`${localeKeyPrefix}.buttons.grabAccount.primaryText`)}
            </span>
            <span className={styles.secondaryText}>
              {I18n.t(`${localeKeyPrefix}.buttons.grabAccount.secondaryText`)}
            </span>
          </div>
        </Button>
        <Button
          id={'gfb-login'}
          className={cx(styles.button, styles.gfb)}
          onClick={this.redirectLogin()}
        >
          <div className={styles.buttonText}>
            <span className={styles.primaryText}>
              {I18n.t(`${localeKeyPrefix}.buttons.gfbAccount.primaryText`)}
            </span>
            <span className={styles.secondaryText}>
              {I18n.t(`${localeKeyPrefix}.buttons.gfbAccount.secondaryText`)}
            </span>
          </div>
        </Button>
      </div>
    </div>
  );

  renderShopifyProcessing = () => (
    <div className={styles.processingPanel}>
      <Spin className={styles.spinner} />
      <div className={styles.message}>
        {I18n.t(`${localeKeyPrefix}.status.authorizing`)}
      </div>
    </div>
  );

  renderError = error => (
    <div className={cx(styles.mainPanelContent, styles.errorPanel)}>
      <p className={styles.primaryText}>
        {I18n.t(`${localeKeyPrefix}.status.somethingWentWrong`)}
      </p>
      {error && <p className={styles.secondaryText}>{error.message}</p>}
    </div>
  );

  renderContent = () => {
    const { error, callbackParams, completing, authorizing } = this.state;
    if (error !== null) {
      return this.renderError(error);
    }

    if (authorizing || completing) {
      return this.renderShopifyProcessing();
    }

    if (callbackParams !== null) {
      if (!isAuthHintEmpty()) {
        return this.renderShopifyProcessing();
      }
      return this.renderShopifyLogin();
    }

    return this.renderError({
      message: I18n.t(`${localeKeyPrefix}.status.invalidRequest`),
    });
  };

  render() {
    return (
      <div className={styles.landing}>
        <div className={styles.mask} />
        <div className={styles.mainPanel}>
          <div className={styles.logo}>
            <img src={shopifyGrabExpress} alt={'shopify'} />
          </div>
          <div className={styles.mainPanelContent}>{this.renderContent()}</div>
        </div>
      </div>
    );
  }
}

export const ShopifyLandingPageComp = ShopifyLandingPage;

export default withRouter(
  connect(state => ({
    userInfo: userInfoSelector(state),
  }))(ShopifyLandingPage),
);
